import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { RecoilState, useRecoilState } from 'recoil';
import { Flex, Select, Text } from '@tq/ui-components/dist/components';
import { CSSObject } from '@tq/ui-components/dist/styled-system';

import { usePermissions } from 'api';
import { Permission } from 'types';

type ManageTeamFilterPermissionSelectProps = {
  permissionState: RecoilState<number>;
  includeAllOption?: boolean;
  includePermissionDetails?: boolean;
  inputId?: string;
  sx?: CSSObject;
  disabled?: boolean;
};

export const ManageTeamFilterPermissionSelect: React.FC<ManageTeamFilterPermissionSelectProps> =
  ({
    permissionState,
    includeAllOption = false,
    includePermissionDetails = false,
    inputId,
    disabled = false,
    sx,
    ...rest
  }) => {
    const { t } = useTranslation('manage_team');
    const { data: permissionData } = usePermissions();
    const [permission, setPermission] = useRecoilState(permissionState);
    const mountedRef = useRef(true);

    useEffect(() => {
      return () => {
        mountedRef.current = false;
      };
    }, []);

    const permissionOptions: { [key: string]: string[] } = {
      [Permission.Admin]: [
        t('permission_admin_1', 'Add and remove users'),
        t('permission_admin_2', 'Manage user permissions and teams'),
        t('permission_admin_3', 'View reports for the organisation'),
        t('permission_admin_4', 'Assign and respond to consultations'),
      ],
      [Permission.Consultee]: [
        t('permission_consultee_1', 'View reports for the organisation'),
        t('permission_consultee_2', 'Assign and respond to consultations'),
      ],
    };

    const selectablePermissions = permissionData?.permissions?.filter((o) => o.value !== 3);

    const selectedPermission = permissionData?.permissions?.find(
      (o) => o.value === permission
    );

    const permissionDetails = selectedPermission
      ? permissionOptions[selectedPermission?.label]
      : undefined;

    const handleSelect = (value: string) => {
      const selectedValue = selectablePermissions?.find((item) => item.value === +value);
      if (
        !mountedRef.current ||
        !selectedValue ||
        Number(value) === selectedPermission?.value
      ) {
        return;
      }

      setPermission(Number(value));
    };

    return (
      <div data-testid='PermissionSelect'>
        {mountedRef.current ? (
          <Select
            inputId={inputId}
            value={permission}
            onChange={(value) => handleSelect(value)}
            sx={sx}
            disabled={disabled}
            aria-label='Permission select dropdown'
            {...rest}
          >
            {includeAllOption ? (
              <option key='all' value={0}>
                {t('all', 'All')}
              </option>
            ) : (
              <option key='select' value={0}>
                {t('permission_placeholder', '- Select -')}
              </option>
            )}

            {selectablePermissions?.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        ) : null}
        {includePermissionDetails && permissionDetails && selectedPermission ? (
          <Flex mt={3} direction='column'>
            <Text fontWeight='bold'>
              {selectedPermission.label} {t('permission_can_text', 'can')}:
            </Text>
            {permissionDetails.map((p) => (
              <Text key={`${p}-${p.length}`}>- {p}</Text>
            ))}
          </Flex>
        ) : null}
      </div>
    );
  };
